import { useRef, useState } from "react";
import { Camera, X } from "lucide-react";

interface Props {
  label?: string;
  maxMb?: number;
  multiple?: boolean;
  onPhotos: (photos: { url: string; name: string; mimeType: string }[]) => void;
  photos?: { url: string; name: string }[];
  onRemove?: (index: number) => void;
}

export function PhotoUploader({ label = "Add photos", maxMb = 10, multiple = true, onPhotos, photos = [], onRemove }: Props) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [error, setError] = useState("");

  async function handleFiles(files: FileList | null) {
    if (!files || !files.length) return;
    setError("");
    const results: { url: string; name: string; mimeType: string }[] = [];
    for (const file of Array.from(files).slice(0, 10)) {
      if (file.size > maxMb * 1024 * 1024) {
        setError(`${file.name} is too large (max ${maxMb}MB)`);
        continue;
      }
      const url = await new Promise<string>((res, rej) => {
        const reader = new FileReader();
        reader.onload = () => res(reader.result as string);
        reader.onerror = rej;
        reader.readAsDataURL(file);
      });
      results.push({ url, name: file.name, mimeType: file.type });
    }
    if (results.length) onPhotos(results);
  }

  return (
    <div className="photo-uploader">
      {label && <span className="photo-uploader-label">{label}</span>}
      <div className="photo-uploader-grid">
        {photos.map((p, i) => (
          <div key={i} className="photo-thumb">
            <img src={p.url} alt={p.name} />
            {onRemove && <button type="button" className="photo-thumb-remove" onClick={() => onRemove(i)}><X size={12} /></button>}
          </div>
        ))}
        <button type="button" className="photo-add-btn" onClick={() => inputRef.current?.click()}>
          <Camera size={20} />
          <span>{multiple ? "Add" : "Choose"}</span>
        </button>
      </div>
      {error && <p className="photo-uploader-error">{error}</p>}
      <input ref={inputRef} type="file" accept="image/jpeg,image/png,image/webp,image/heic" multiple={multiple}
        style={{ display: "none" }} onChange={e => handleFiles(e.target.files)} />
    </div>
  );
}
